import { LocalStorageEmulatedMutex } from "./local-storage-emulated-mutex";
import { nonStandardPushErrorEvent } from "./non-standard-logging";

const terminalKEY = "TERMINAL";
const terminalExistsKey = "TERMINAL_EXISTS";
const generalPrefix = "SDHGKM_";
const initialPoolLength = 50;
const mutexes: LocalStorageEmulatedMutex[] = [];
const defaultMaximumLocalStorageRange = 1000;

export class HeuristicallyGeneratedLocalStorageKeyMap {
  constructor(private storagePrefix: string, private maximumUpperBounds: number = defaultMaximumLocalStorageRange) {
    // Initializes the terminal key for this map {initialPoolLength} indexes ahead of the start key (0).
    // Should not reinitialize the terminal key.
    let terminalAlreadyCreatedKey = this.getGeneralPrefix(terminalExistsKey);
    if (!localStorage.getItem(terminalAlreadyCreatedKey)) {
      // No terminal? Let's fix that for you.
      let terminalStartLocation = this.getHeuristicKey(initialPoolLength);
      localStorage.setItem(terminalStartLocation, terminalKEY);
      localStorage.setItem(terminalAlreadyCreatedKey, true.toString());
    }
  }

  public clear() {
    // Removes all entries that would be scoped to this KeyMap, and nothing more.
    localStorage.removeItem(this.getGeneralPrefix(terminalExistsKey));
    for (let i = 0; i <= this.maximumUpperBounds; i++) {
      let key = this.getHeuristicKey(i);
      if (localStorage.getItem(key) == terminalKEY) {
        localStorage.removeItem(key);
        break;
      } else {
        localStorage.removeItem(key);
      }
    }
  }

  public static runMemoryLeakingGlobalCleanup() {
    // This method claims memory on Edge (.key). Not significant if used a small constant number of times (e.g. once), but if used many times will be a noticeable leak.
    let keysToTrash = [];
    for (let i = 0; i < localStorage.length; i++) {
      let key = localStorage.key(i);
      if (key.startsWith(generalPrefix)) {
        keysToTrash.push(key);
      }
    }

    keysToTrash.forEach((keyToTrash) => {
      localStorage.removeItem(keyToTrash);
    });
  }

  public trackKey(keyToTrack: string) {
    let nextSlot = this.getNextSlot();
    localStorage.setItem(nextSlot, keyToTrack);
  }

  public safeTrackKey(keyToTrack: string, windowId: string): Promise<void> {
    let mutex = mutexes.find((mutex) => {
      return mutex.instanceIdentifier == windowId && mutex.prefix == this.storagePrefix;
    });

    if (!mutex) {
      mutex = new LocalStorageEmulatedMutex(this.storagePrefix, windowId);
      mutexes.push(mutex);
    }

    return mutex
      .executeLockedCode(() => {
        this.trackKey(keyToTrack);
      })
      .catch((onrejected) => {
        nonStandardPushErrorEvent("local-storage-key-map", onrejected, { isUnhandled: true });
        return Promise.reject(onrejected);
      });
  }

  public entries(): Map<string, string> {
    let entryMap = new Map<string, string>();
    let index = 0;
    while (true) {
      let key = this.getHeuristicKey(index++);
      let pointerKey = localStorage.getItem(key);
      if (pointerKey == terminalKEY || index > this.maximumUpperBounds) {
        if (index > this.maximumUpperBounds) {
          nonStandardPushErrorEvent("local-storage-key-map", "TerminalKey has exceeded maximum upper bounds.", {
            isUnhandled: true
          });
        }
        break;
      }

      if (pointerKey) {
        let pointerValue = localStorage.getItem(pointerKey);
        if (pointerValue) {
          entryMap.set(pointerKey, pointerValue);
        } else {
          localStorage.removeItem(key);
        }
      }
    }

    return entryMap;
  }

  private getGeneralPrefix(postfix: string) {
    return `${generalPrefix}${this.storagePrefix}_${postfix}`;
  }

  private getHeuristicKey(index: number) {
    return this.getGeneralPrefix(index.toString());
  }

  private getNextSlot(): string {
    let index = 0;
    while (true) {
      let key = this.getHeuristicKey(index++);
      let pointerKey = localStorage.getItem(key);
      if (pointerKey == terminalKEY) {
        localStorage.setItem(this.getHeuristicKey(index), terminalKEY);
        return key;
      }
      if (!pointerKey) {
        return key;
      }
      let pointerValue = localStorage.getItem(pointerKey);
      if (!pointerValue) {
        return key;
      }
    }
  }
}
